// src/pages/operations-dashboard/components/KpiCards.jsx
import React from "react";
import {
  ShoppingBag,
  IndianRupee,
  Truck,
  PackageCheck,
  PackageX,
  AlertTriangle,
  Wallet,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { formatCurrency, formatCompact, getChangePercent } from "../../../utils/dashboardConstants";

/**
 * Animated number display for KPI values.
 */
const AnimatedValue = ({ value, prefix = "", suffix = "" }) => (
  <span className="tabular-nums">
    {prefix}{value}{suffix}
  </span>
);

/**
 * Change badge — shows up/down percentage.
 */
const ChangeBadge = ({ current, previous, invertColors = false }) => {
  if (previous === null || previous === undefined) return null;
  const pct = getChangePercent(
    typeof current === "string" ? parseFloat(current) : current,
    typeof previous === "string" ? parseFloat(previous) : previous
  );

  if (pct === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-500">
        <Minus className="w-3 h-3" />
        0%
      </span>
    );
  }

  const isPositive = invertColors ? pct < 0 : pct > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-2 py-0.5 text-xs font-semibold rounded-full ${
        isPositive ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
      }`}
    >
      {pct > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

/**
 * Single KPI Card component.
 */
const KpiCard = ({ title, value, subtitle, icon: Icon, iconBg, iconColor, trend, trendPrev, invertTrend = false, isLoading = false }) => (
  <div className="group relative bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-xl hover:shadow-gray-100/50 hover:border-gray-200 transition-all duration-300 hover:-translate-y-0.5">
    {/* Subtle gradient overlay on hover */}
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-transparent to-transparent group-hover:from-blue-50/30 group-hover:to-purple-50/20 transition-all duration-300 pointer-events-none" />

    <div className="relative">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2.5 rounded-xl ${iconBg}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={2} />
        </div>
        {trend !== undefined && <ChangeBadge current={trend} previous={trendPrev} invertColors={invertTrend} />}
      </div>

      {/* Value */}
      {isLoading ? (
        <div className="space-y-2">
          <div className="h-8 bg-gray-100 rounded-lg animate-pulse w-24" />
          <div className="h-4 bg-gray-50 rounded animate-pulse w-16" />
        </div>
      ) : (
        <>
          <p className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
            <AnimatedValue value={value} />
          </p>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </>
      )}
    </div>
  </div>
);

/**
 * KPI Cards Grid — Top-level summary section.
 */
export default function KpiCards({ summary, loading }) {
  const isLoading = loading?.summary;

  const cards = [
    {
      title: "Today's Orders",
      value: formatCompact(summary?.orders_today || 0),
      icon: ShoppingBag,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-500",
      trend: summary?.orders_today,
      trendPrev: summary?.orders_yesterday,
      subtitle: `Yesterday: ${formatCompact(summary?.orders_yesterday || 0)}`,
    },
    {
      title: "Today's Revenue",
      value: formatCurrency(summary?.revenue_today || 0),
      icon: IndianRupee,
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-500",
      trend: summary?.revenue_today,
      trendPrev: summary?.revenue_yesterday,
      subtitle: `Yesterday: ${formatCurrency(summary?.revenue_yesterday || 0)}`,
    },
    {
      title: "Delivered Today",
      value: formatCompact(summary?.delivered_today || 0),
      icon: PackageCheck,
      iconBg: "bg-green-50",
      iconColor: "text-green-500",
      subtitle: "Successful deliveries",
    },
    {
      title: "RTO Today",
      value: formatCompact(summary?.rto_today || 0),
      icon: PackageX,
      iconBg: "bg-red-50",
      iconColor: "text-red-500",
      invertTrend: true,
      subtitle: "Return to origin",
    },
    {
      title: "NDR Pending",
      value: formatCompact(summary?.ndr_pending_action || 0),
      icon: AlertTriangle,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-500",
      invertTrend: true,
      subtitle: "Requires attention",
    },
    {
      title: "This Week Orders",
      value: formatCompact(summary?.orders_this_week || 0),
      icon: TrendingUp,
      iconBg: "bg-violet-50",
      iconColor: "text-violet-500",
      subtitle: `Month: ${formatCompact(summary?.orders_this_month || 0)}`,
    },
    {
      title: "This Week Revenue",
      value: formatCurrency(summary?.revenue_this_week || 0),
      icon: IndianRupee,
      iconBg: "bg-teal-50",
      iconColor: "text-teal-500",
      subtitle: `Month: ${formatCurrency(summary?.revenue_this_month || 0)}`,
    },
    {
      title: "Wallet Balance",
      value: formatCurrency(summary?.wallet_balance || 0),
      icon: Wallet,
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-500",
      subtitle: `Available: ${formatCurrency(summary?.wallet_available_balance || 0)}`,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, i) => (
        <KpiCard key={i} {...card} isLoading={isLoading} />
      ))}
    </div>
  );
}
